// One product block on a document — the commonsku-matched layout the CEO
// approved (design preview artifact ef96cb3f, 2026-07-19).
//
//   LEFT   small thumbnail + SKU + supplier + product name + full description
//   RIGHT  itemised table: Item | Units | Cost | Margin | Price | Tax | Amount (Stephanie 2026-10-26)
//            · one PRICED ROW PER COLOUR (Blue $4.98, Dark Green $4.14 — colours
//              genuinely carry different prices)
//            · bold "Total units" row
//            · italic imprint-location header, with its charge rows beneath
//            · per-product "Total" row carrying margin % and tax code
//
// The same component serves the REP view (editable in place) and the CLIENT view
// (read-only, no rep-only figures) so the two cannot drift apart — that drift is
// the whole reason this redesign exists.
//
// Money never computed locally: every amount comes from the shared integer-cents
// module (packages/shared) that the server bills from.
import { Lock, LockOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  formatCents,
  formatMills,
  formatMillsParts,
  formatPct,
  costInputValue,
  isValidCostInput,
  isValidMoneyInput,
  toMills,
  normalizeMoneyInput,
  normalizeQuantityInput,
  lineChargesCents,
  lineGoodsCents,
  lineCostCents,
  lineMarginPct,
  lineTotalCents,
  marginHundredths,
  isPriceableMargin,
  marginRepriceWrites,
  sellPriceFromCostMills,
  keepMarginSellPrice,
  hasUsableCostBasis,
  toCents,
} from "./money";
import { chargeQuantity, divideRoundHalfUp, rateHundredths } from "@merchd/shared";
import {
  docLineImageUrl,
  docLineTitle,
  docVariantLabel,
  type DocLine,
  type DocVariant,
} from "./types";
import { cn } from "@/lib/utils";
import { colorCountWords } from "@merchd/shared";

/** The money shape the shared math wants off a document line. */
function toMoneyLine(line: DocLine) {
  return {
    unitPrice: line.unitPrice,
    quantityTotal: line.quantityTotal,
    variants: line.variants.map((v) => ({
      quantity: v.quantity,
      unitPrice: v.unitPrice,
      unitCost: v.unitCost,
    })),
    decorationCharges: (line.decorations ?? []).flatMap((d) =>
      d.charges.map((c) => ({ amount: c.amount, billingModel: c.billingModel, quantity: c.quantity ?? null })),
    ),
    netCost: line.netCost ?? "0",
  };
}

/**
 * Money/quantity input handling lives in the SHARED module (packages/shared)
 * so the offline gate can execute it directly rather than pattern-match this
 * source — the "4,98 -> 498" bug below was invisible to a source lint.
 * MONEY_INPUT_RE there is character-identical to the API's own regex.
 */
export function isValidMoney(v: string): boolean {
  return isValidMoneyInput(v);
}
/** A price cell may also be cleared, which unsets the per-colour price. */
export function isValidMoneyOrBlank(v: string): boolean {
  return normalizeMoneyInput(v) === "" || isValidMoneyInput(v);
}
/** A COST cell takes three decimals (2026-08-11) — suppliers publish them and
 *  the column is numeric(12,3). Kept as its own pair of predicates rather than
 *  widening isValidMoney, so a PRICE cell still refuses 1.692 and no
 *  client-facing figure gains a decimal. */
export function isValidCost(v: string): boolean {
  return isValidCostInput(v);
}
export function isValidCostOrBlank(v: string): boolean {
  return normalizeMoneyInput(v) === "" || isValidCostInput(v);
}
/** A margin a price can actually be derived from. 100%+ is impossible (sell
 *  would be infinite); negative is allowed — selling below cost is a real,
 *  deliberate call a rep sometimes makes and the document must be able to say
 *  so. Blank clears nothing: margin isn't stored, it's derived from cost+sell. */
export function isValidMargin(v: string): boolean {
  const t = v.trim().replace(/%$/, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return false;
  return isPriceableMargin(marginHundredths(t));
}
export function isValidQuantity(v: string): boolean {
  const s = normalizeQuantityInput(v);
  const n = Number(s);
  return s !== "" && Number.isInteger(n) && n >= 0;
}

/**
 * A cost figure whose DECIMAL POINT stays put whether or not the third place is
 * significant.
 *
 * ⚠️ Right-alignment plus `tabular-nums` is not enough: tabular figures equalise
 * digit WIDTH, but "$4.98" and "$1.692" are different LENGTHS, so right-aligning
 * lands the "." in two different places down a column. Beside a Price column
 * that is always 2dp, that reads as a formatting bug rather than as precision.
 * The third digit gets a reserved 1ch slot, rendered empty when it is a
 * non-significant zero, so every row's point lines up.
 */
function CostFigure({
  mills,
  currency,
}: {
  mills: number;
  currency: string;
}) {
  const { base, thousandth } = formatMillsParts(mills, currency);
  return (
    <span className="inline-flex justify-end tabular-nums">
      <span>{base}</span>
      <span className="inline-block w-[1ch] text-left">{thousandth}</span>
    </span>
  );
}

/** A charge's label — "Setup fee", "Run charge" — from its enum value. */
function chargeLabel(chargeType: string, note: string | null): string {
  if (note && note.trim() !== "") return note.trim();
  const map: Record<string, string> = {
    setup: "Setup fee",
    run: "Run charge",
    digitizing: "Digitizing",
    rush: "Rush charge",
    screen: "Screen charge",
    pms_match: "PMS match",
    other: "Charge",
  };
  return map[chargeType] ?? "Charge";
}

/**
 * A number cell that only commits on blur/Enter, so a rep typing "12" doesn't
 * fire a PATCH per keystroke (and doesn't fight the refetch that follows).
 */
/** "0", "0.00", "$0" … — a box showing nothing real yet. */
function isZero(v: string): boolean {
  const t = v.trim().replace(/^\$/, "");
  return t !== "" && Number(t) === 0;
}

/** The Shipping item's name, typed over in place — "Ground shipping",
 *  "Overnight"… (Stephanie 2026-10-27). Saves on blur / Enter; Escape or an
 *  empty box puts the old name back. Same Escape-ref rule as EditableCell. */
function EditableLineTitle(props: { value: string; onCommit: (next: string) => void }) {
  const { value, onCommit } = props;
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const skipCommitRef = useRef(false);
  const commit = (): void => {
    const next = draft.replace(/\s+/g, " ").trim();
    if (skipCommitRef.current || next === "" || next === value) {
      skipCommitRef.current = false;
      setDraft(value);
      return;
    }
    onCommit(next);
  };
  return (
    <input
      type="text"
      value={draft}
      maxLength={200}
      aria-label="Shipping name"
      title="Rename — e.g. Ground shipping, Overnight"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          skipCommitRef.current = true;
          e.currentTarget.blur();
        }
      }}
      className="-mx-1 w-full max-w-md rounded border border-transparent bg-transparent px-1 text-[17px] font-bold leading-snug text-brown-500 hover:border-rule focus:border-rule focus:bg-white focus:outline-none"
    />
  );
}

/** The Shipping item's note for the client ("Ships in 2 boxes", "Allow 1
 *  extra day for Alaska"), shown under its name on the client's quote
 *  (Stephanie 2026-10-27). Saves on blur; Escape puts the old note back. */
function EditableLineNote(props: { value: string; onCommit: (next: string | null) => void }) {
  const { value, onCommit } = props;
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const skipCommitRef = useRef(false);
  const commit = (): void => {
    const next = draft.trim();
    if (skipCommitRef.current || next === value.trim()) {
      skipCommitRef.current = false;
      setDraft(value);
      return;
    }
    onCommit(next === "" ? null : next);
  };
  return (
    <label className="mt-2 block">
      <span className="text-[12px] font-semibold text-brown-400">Description — the client sees this on their quote</span>
      <textarea
        value={draft}
        rows={2}
        maxLength={2000}
        placeholder="e.g. Ships in 2 boxes · allow 1 extra day for delivery"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            skipCommitRef.current = true;
            e.currentTarget.blur();
          }
        }}
        className="mt-1 w-full rounded-md border border-rule bg-white px-3 py-2 text-[14px] leading-relaxed text-brown-500 placeholder:text-brown-300 focus:border-brown-300 focus:outline-none"
      />
    </label>
  );
}

export function EditableCell(props: {
  value: string;
  onCommit: (next: string) => void;
  /** Reject a draft before it reaches the server. Invalid input reverts. */
  validate?: (next: string) => boolean;
  disabled?: boolean;
  prefix?: string;
  ariaLabel: string;
}) {
  const { value, onCommit, validate, disabled, prefix, ariaLabel } = props;
  const [draft, setDraft] = useState(value);
  // Re-sync when the server value changes underneath us (refetch, another edit).
  useEffect(() => setDraft(value), [value]);

  // ESCAPE MUST CANCEL, AND A REF IS REQUIRED FOR THAT — do not "simplify" this
  // to setDraft(value) + blur(). setDraft is asynchronous, but .blur() fires the
  // native blur event SYNCHRONOUSLY in the same call stack, so the still-mounted
  // onBlur closure reads the PRE-Escape draft and commits the very value the rep
  // was trying to discard (adversarial review reproduced this in a React
  // harness: typing 9999 into a $4.98 price and pressing Escape PATCHed 9999,
  // then visually reverted, hiding what it had done). Refs update synchronously,
  // so this flag is readable by that same-stack blur handler.
  const skipCommitRef = useRef(false);

  const commit = (): void => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      setDraft(value);
      return;
    }
    if (draft === value) return;
    // A zero box that was emptied on focus and left blank: nothing changed.
    if (draft.trim() === "" && isZero(value)) {
      setDraft(value);
      return;
    }
    // Invalid input never reaches the server; the field snaps back instead of
    // leaving a bad number sitting in a price box.
    if (validate && !validate(draft)) {
      setDraft(value);
      return;
    }
    onCommit(draft);
  };

  return (
    <span className="inline-flex items-center justify-end gap-0.5">
      {prefix && <span className="text-brown-300">{prefix}</span>}
      <input
        value={draft}
        disabled={disabled}
        aria-label={ariaLabel}
        inputMode="decimal"
        onChange={(e) => setDraft(e.target.value)}
        // Typing replaces the number instead of adding to it (Stephanie
        // 2026-10-14: "dont leave the 0 in there"): a zero box empties on
        // focus, any other number is selected.
        onFocus={(e) => {
          if (isZero(draft)) setDraft("");
          else e.currentTarget.select();
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            // Flag BEFORE blur() — the blur handler runs synchronously below.
            skipCommitRef.current = true;
            e.currentTarget.blur();
          }
        }}
        className={cn(
          "w-full min-w-0 rounded border border-brown-100 bg-white px-2 py-1 text-right text-sm font-semibold tabular-nums text-brown-500",
          "focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200",
          disabled && "cursor-not-allowed border-transparent bg-transparent",
        )}
      />
    </span>
  );
}

export interface DocumentLineCardProps {
  line: DocLine;
  currency?: string;
  /** False hides every money column (the "Hide totals" toggle + client copy). */
  showMoney?: boolean;
  /** Rep-only figures: the Margin column. Never true on a client-facing view. */
  showRepDetail?: boolean;
  /** The order's sales-tax rate (0..100). When given, the Tax column shows this
   *  item's tax in dollars instead of a code (Stephanie 2026-10-14). */
  taxRatePct?: string | null;
  /** Enables in-place editing of quantities and per-colour prices. */
  editable?: boolean;
  onPatchLine?: (body: Record<string, unknown>) => void;
  onPatchVariant?: (variantId: string, body: Record<string, unknown>) => void;
  /** Clicking anywhere on the item opens it (Stephanie 2026-10-14: "I want
   *  the entire item box to be clickable"). Clicks on its own boxes, buttons
   *  and links keep doing their own thing. */
  onOpen?: () => void;
  /** Rendered top-right of the card (remove button, drag handle, etc). */
  actions?: React.ReactNode;
  /** Client-facing only: notes that the photo is indicative, not the exact
   *  decorated item. The page this component replaced carried this caption and
   *  it protects against a client treating a stock photo as a proof. */
  imageIsForReference?: boolean;
  /** Rendered under the quantity rows — the staff quote puts the colour/size
   *  editor here. Never passed on a client-facing view. */
  belowQuantities?: React.ReactNode;
  /** commonsku's lock on Retail (Stephanie 2026-10-14). Locked (the default
   *  here, so the quote page behaves as before): a cost change keeps the price
   *  and the margin moves. Unlocked (the item window's default): a cost change
   *  keeps the row's margin and re-prices it. */
  retailLocked?: boolean;
  /** Shows the lock next to "Price" on the quote page (Stephanie 2026-10-27);
   *  omitted = no lock button (client copies, read-only views). */
  onToggleRetailLock?: () => void;
  /** Staff only: shown under the title, e.g. "Billed on invoice #50231" for an
   *  item copied onto another quote's invoice (Stephanie 2026-10-16). */
  badge?: React.ReactNode;
}

export function DocumentLineCard(props: DocumentLineCardProps) {
  const {
    line,
    currency = "USD",
    showMoney = true,
    showRepDetail = false,
    onOpen,
    taxRatePct,
    editable = false,
    onPatchLine,
    onPatchVariant,
    actions,
    imageIsForReference = false,
    belowQuantities,
    retailLocked = true,
    onToggleRetailLock,
  } = props;

  const moneyLine = toMoneyLine(line);
  const imageUrl = docLineImageUrl(line);
  const title = docLineTitle(line);
  // Staff: our SKU first, then the supplier's part number (for buying).
  // Client: productSnapshot.sku already IS our SKU and no catalogueSku comes.
  const ourSku = line.catalogueSku?.trim();
  const supplierSku = line.productSnapshot?.sku?.trim();
  const sku = [ourSku, supplierSku && supplierSku !== ourSku ? supplierSku : null].filter(Boolean).join(" · ") || undefined;
  const supplier = line.productSnapshot?.supplierName?.trim();
  const description = line.description?.trim();
  // Shipping item: its name is typed over in place and its description is a
  // note for the client (Stephanie 2026-10-27).
  const hasOwnName = !!line.productSnapshot?.name?.trim();
  const editableShipping = editable && !!onPatchLine && line.serviceType === "shipping";

  const goodsCents = lineGoodsCents(moneyLine);
  const chargesCents = lineChargesCents(moneyLine);
  const totalCents = lineTotalCents(moneyLine);
  // Only derived when the rep gate is on: a client payload carries no cost,
  // so margin is not merely hidden — it is not computable.
  const marginPct = showRepDetail ? lineMarginPct(moneyLine) : null;

  const hasVariants = line.variants.length > 0;
  // Colour rows fall back to the line price when they carry none of their own —
  // exactly what the money math does, so the rows always sum to the total shown.
  const linePriceCents = toCents(line.unitPrice);
  const lineCostEachMills = toMills(line.netCost ?? 0);
  // Margin is DERIVED FROM COST — with no cost on file there is nothing to
  // derive from, so the margin cell must reject the entry (not silently no-op).
  // Sourced from the shared module rather than re-implemented here: review
  // flagged this as a second independent copy of the same rule, which is the
  // exact duplicate-rule drift behind every other defect in this feature.
  const hasUsableCost = hasUsableCostBasis(moneyLine);
  const costCents = lineCostCents(moneyLine);

  /**
   * Apply a target MARGIN by deriving the sell price from cost.
   *
   * Set every priced row to the target rather than scaling the line: if each
   * row satisfies sell = cost / (1 - m), the line total satisfies it too
   * (Σsell = Σcost / (1 - m)), so the margin the rep typed is the margin the
   * document reports back — to within a cent's rounding per row.
   *
   * NOT zero drift, and the earlier claim that it was is corrected here:
   * prices are whole cents, so a target margin is not always exactly
   * representable. Measured drift is bounded and small (well under 0.35pp
   * across realistic costs — the gate asserts that bound), e.g. $12.99 at
   * 12.50% prices to $14.85 and reads back 12.53%. That is inherent to integer
   * money, not a defect; the displayed margin is always recomputed from the
   * cost and price actually stored, so it never lies about what is on file.
   */
  const applyMargin = (raw: string): void => {
    const marginH = marginHundredths(raw.trim().replace(/%$/, ""));
    if (marginH == null) return;
    // The DECISION — which prices to write, and from what basis — lives in the
    // shared module (marginRepriceWrites) so the offline gate can EXECUTE it.
    // It broke three times in three review iterations while it lived here, each
    // time writing a price with no cost behind it, and each time invisible to a
    // source lint. This function now only dispatches the plan.
    const writes = marginRepriceWrites(moneyLine, marginH);
    for (const w of writes) {
      if (w.variantIndex === null) {
        onPatchLine?.({ unit_price: w.unitPrice });
      } else {
        const v = line.variants[w.variantIndex];
        if (v) onPatchVariant?.(v.id, { unit_price: w.unitPrice });
      }
    }
  };
  const decorationsWithContent = (line.decorations ?? []).filter(
    (d) => (d.location && d.location.trim() !== "") || d.charges.length > 0 || d.artwork != null,
  );

  // Column template shared by every row so the grid lines up down the card.
  // Rep view carries Cost + Margin; the client's copy has neither (and its
  // payload contains no cost at all, so they are not merely hidden).
  const cols = showMoney
    ? showRepDetail
      ? "grid-cols-[minmax(0,1fr)_84px_108px_88px_108px_96px_128px]"
      : "grid-cols-[minmax(0,1fr)_84px_112px_128px]"
    : "grid-cols-[minmax(0,1fr)_84px]";

  return (
    <div
      className={cn(
        // Each item is its own card with space around it (Stephanie
        // 2026-10-26: "it looks like 1 long run on item").
        "mx-3 my-3 rounded-lg border border-brown-200 bg-white px-4 py-4 shadow-sm",
        onOpen && "cursor-pointer transition-colors hover:bg-cream-50/60",
      )}
      onClick={onOpen ? (e) => openUnlessControl(e, onOpen) : undefined}
      title={onOpen ? "Open this item — description, images, pricing, imprint" : undefined}
    >
      {/* ── Header strip: thumbnail + name + inline meta, split from the
           table by a strong bottom border (CEO-approved layout 2026-07-21).
           The numbers used to be squeezed beside a ~300px info column; the
           table is now full width so the money reads large. ────────────── */}
      <div className="flex items-start gap-3 border-b-2 border-brown-200 pb-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-cream-100">
          {imageUrl ? (
            <img src={imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-1 text-center text-[9px] uppercase tracking-wide text-brown-300">
              {line.serviceType === "shipping"
                ? "Shipping"
                : line.serviceType !== "product"
                  ? "Service"
                  : "No image"}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {editableShipping ? (
            // The name lives in the snapshot; an older line whose name WAS its
            // description moves it there and clears the description, so the
            // old name never shows up as a client note.
            <EditableLineTitle value={title} onCommit={(next) => onPatchLine!(hasOwnName ? { name: next } : { name: next, description: null })} />
          ) : (
            <p className="text-[17px] font-bold leading-snug text-brown-500">{title}</p>
          )}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[13px] font-semibold">
            {sku && <span className="text-muted">{sku}</span>}
            {sku && supplier && <span className="text-muted">·</span>}
            {supplier && <span className="text-rust-500">{supplier}</span>}
            {/* No unit count on a shipping line; "1 unit", not "1 units" (Stephanie 2026-10-26). */}
            {line.serviceType !== "shipping" && (
              <>
                {(sku || supplier) && <span className="text-muted">·</span>}
                <span className="text-brown-400">
                  {line.quantityTotal.toLocaleString()} {line.quantityTotal === 1 ? "unit" : "units"}
                </span>
              </>
            )}
          </div>
          {props.badge ? <div className="mt-1">{props.badge}</div> : null}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>

      {/* ── Description: one full-width line, not a skinny column ───────── */}
      {editableShipping ? (
        <EditableLineNote
          value={hasOwnName ? (line.description ?? "") : ""}
          onCommit={(next) => onPatchLine!(hasOwnName ? { description: next } : { name: title, description: next })}
        />
      ) : (
        description && description !== title && (
          <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-brown-400">{description}</p>
        )
      )}
      {imageIsForReference && imageUrl && (
        <p className="mt-1.5 text-[12px] italic text-brown-300">
          Product image for reference only.
        </p>
      )}

      {/* ── Itemised table: full width, larger type ────────────────────── */}
      <div className="mt-3 min-w-0 text-brown-500">
        {/* Column labels sit right above each item's pricing, so they're
            still there when you scroll (Stephanie 2026-10-26). */}
        <div className={cn("grid gap-2 border-b border-brown-100 pb-1 text-[11px] font-bold uppercase tracking-wide text-brown-300", cols)}>
          <span />
          <span className="text-right">Units</span>
          {showMoney && showRepDetail && <span className="text-right">Cost</span>}
          {showMoney && showRepDetail && <span className="text-right">Margin</span>}
          {showMoney && (
            <span className="flex items-center justify-end gap-1">
              Price
              {onToggleRetailLock && (
                <button
                  type="button"
                  onClick={onToggleRetailLock}
                  aria-pressed={retailLocked}
                  aria-label={retailLocked ? "Unlock price" : "Lock price"}
                  title={
                    retailLocked
                      ? "Price locked: changing a cost keeps the price, the margin moves. Click to unlock."
                      : "Price unlocked: changing a cost keeps the margin and re-prices. Click to lock."
                  }
                  className={retailLocked ? "text-rust-500" : "text-brown-300 hover:text-brown-500"}
                >
                  {retailLocked ? <Lock className="h-3 w-3" /> : <LockOpen className="h-3 w-3" />}
                </button>
              )}
            </span>
          )}
          {showMoney && showRepDetail && <span className="text-right">Tax</span>}
          {showMoney && <span className="text-right">Amount</span>}
        </div>
        {/* Colour / size rows — each independently priced. */}
        {hasVariants ? (
          line.variants.map((v: DocVariant) => {
            const priced = v.unitPrice != null && v.unitPrice !== "";
            const rowPriceCents = priced ? toCents(v.unitPrice) : linePriceCents;
            const costed = v.unitCost != null && v.unitCost !== "";
            // Mills, not cents: the server books this cost at three decimals, so a
            // cents-rounded display would show the rep 1.69 for a 1.692 cost and
            // the two would silently disagree.
            const rowCostMills = costed ? toMills(v.unitCost) : lineCostEachMills;
            return (
              <div key={v.id} className={cn("grid items-center gap-2 py-1 text-[16px] tabular-nums", cols)}>
                <span className="truncate">{docVariantLabel(v)}</span>
                <span className="text-right">
                  {editable && onPatchVariant ? (
                    <EditableCell
                      value={String(v.quantity)}
                      ariaLabel={`Quantity for ${docVariantLabel(v)}`}
                      validate={isValidQuantity}
                      onCommit={(next) =>
                        onPatchVariant(v.id, {
                          quantity: Number(normalizeQuantityInput(next)),
                        })
                      }
                    />
                  ) : (
                    v.quantity.toLocaleString()
                  )}
                </span>
                {showMoney && showRepDetail && (
                  <span className="text-right">
                    {editable && onPatchVariant ? (
                      <EditableCell
                        // A color with no cost of its own shows the item's cost
                        // (Stephanie 2026-10-26: "the cost and margin disappeared").
                        value={costed ? costInputValue(v.unitCost) : costInputValue(line.netCost)}
                        prefix="$"
                        ariaLabel={`Cost for ${docVariantLabel(v)}`}
                        validate={isValidCostOrBlank}
                        onCommit={(next) => {
                          const trimmed = normalizeMoneyInput(next);
                          // Blank clears the per-colour cost → falls back to the
                          // line cost (never silently zero, which would read as
                          // 100% margin).
                          const body: Record<string, unknown> = {
                            unit_cost: trimmed === "" ? null : trimmed,
                          };
                          // Retail unlocked: keep this row's margin, re-price it.
                          if (!retailLocked) {
                            const newMills = trimmed === "" ? lineCostEachMills : toMills(trimmed);
                            const sell = keepMarginSellPrice(rowCostMills, rowPriceCents, newMills);
                            if (sell != null) body.unit_price = sell;
                          }
                          onPatchVariant(v.id, body);
                        }}
                      />
                    ) : (
                      <CostFigure mills={rowCostMills} currency={currency} />
                    )}
                  </span>
                )}
                {showMoney && showRepDetail && (
                  <span className="text-right text-[14px] text-brown-300">
                    {editable && onPatchVariant ? (
                      // Type a margin → this row's price from its own cost
                      // (Stephanie 2026-10-14, like commonsku). No cost → the
                      // entry is refused and snaps back; a price is never invented.
                      <EditableCell
                        value={
                          rowPriceCents > 0 && rowCostMills > 0
                            ? (((rowPriceCents * 10 - rowCostMills) / (rowPriceCents * 10)) * 100).toFixed(2)
                            : ""
                        }
                        ariaLabel={`Margin for ${docVariantLabel(v)}`}
                        validate={(m) => isValidMargin(m) && rowCostMills > 0}
                        onCommit={(m) => {
                          const marginH = marginHundredths(m.trim().replace(/%$/, ""));
                          const sell = marginH == null ? null : sellPriceFromCostMills(rowCostMills, marginH);
                          if (sell != null) onPatchVariant(v.id, { unit_price: sell });
                        }}
                      />
                    ) : rowPriceCents > 0 && rowCostMills > 0 ? (
                      formatPct(
                        ((rowPriceCents * 10 - rowCostMills) / (rowPriceCents * 10)) * 100,
                      )
                    ) : (
                      ""
                    )}
                  </span>
                )}
                {showMoney && (
                  <span className="text-right">
                    {editable && onPatchVariant ? (
                      <EditableCell
                        // …and the item's price, like the item window does.
                        value={priced ? String(v.unitPrice) : line.unitPrice}
                        prefix="$"
                        ariaLabel={`Price for ${docVariantLabel(v)}`}
                        validate={isValidMoneyOrBlank}
                        onCommit={(next) => {
                          const trimmed = normalizeMoneyInput(next);
                          // Empty clears the per-colour price → falls back to
                          // the line price (never silently free).
                          onPatchVariant(v.id, {
                            unit_price: trimmed === "" ? null : trimmed,
                          });
                        }}
                      />
                    ) : (
                      formatCents(rowPriceCents, currency)
                    )}
                  </span>
                )}
                {showMoney && showRepDetail && <span className="text-right" />}
                {showMoney && (
                  <span className="text-right font-semibold">
                    {formatCents(rowPriceCents * v.quantity, currency)}
                  </span>
                )}
              </div>
            );
          })
        ) : (
          // No colour breakdown — a single row at the line price.
          <div className={cn("grid items-center gap-2 py-1 text-[16px] tabular-nums", cols)}>
            <span className="truncate">{line.serviceType === "shipping" ? title : description || title}</span>
            <span className="text-right">
              {editable && onPatchLine ? (
                <EditableCell
                  value={String(line.quantityTotal)}
                  ariaLabel="Quantity"
                  validate={isValidQuantity}
                  onCommit={(next) =>
                    onPatchLine({
                      quantity_total: Number(normalizeQuantityInput(next)),
                    })
                  }
                />
              ) : (
                line.quantityTotal.toLocaleString()
              )}
            </span>
            {showMoney && showRepDetail && (
              <span className="text-right">
                {editable && onPatchLine ? (
                  <EditableCell
                    value={costInputValue(line.netCost)}
                    prefix="$"
                    ariaLabel="Unit cost"
                    validate={isValidCost}
                    onCommit={(next) => {
                      const cost = normalizeMoneyInput(next);
                      const body: Record<string, unknown> = { net_cost: cost };
                      // Retail unlocked: keep the margin, re-price from the new cost.
                      if (!retailLocked) {
                        const sell = keepMarginSellPrice(lineCostEachMills, linePriceCents, toMills(cost));
                        if (sell != null) body.unit_price = sell;
                      }
                      onPatchLine(body);
                    }}
                  />
                ) : (
                  <CostFigure mills={lineCostEachMills} currency={currency} />
                )}
              </span>
            )}
            {showMoney && showRepDetail && (
              <span className="text-right text-[14px] text-brown-300">
                {editable && onPatchLine ? (
                  <EditableCell
                    value={marginPct == null ? "" : marginPct.toFixed(2)}
                    ariaLabel="Margin"
                    validate={(m) => isValidMargin(m) && hasUsableCost}
                    onCommit={applyMargin}
                  />
                ) : (
                  formatPct(marginPct)
                )}
              </span>
            )}
            {showMoney && (
              <span className="text-right">
                {editable && onPatchLine ? (
                  <EditableCell
                    value={line.unitPrice}
                    prefix="$"
                    ariaLabel="Unit price"
                    validate={isValidMoney}
                    onCommit={(next) =>
                      onPatchLine({ unit_price: normalizeMoneyInput(next) })
                    }
                  />
                ) : (
                  formatCents(linePriceCents, currency)
                )}
              </span>
            )}
            {showMoney && showRepDetail && <span className="text-right" />}
            {showMoney && (
              <span className="text-right font-semibold">
                {formatCents(goodsCents, currency)}
              </span>
            )}
          </div>
        )}

        {/* Total units */}
        {hasVariants && (
          <div className={cn("mt-1 grid items-center gap-2 border-t border-brown-100 pt-1.5 text-[16px] font-bold tabular-nums", cols)}>
            <span>Total units</span>
            <span className="text-right">{line.quantityTotal.toLocaleString()}</span>
            {showMoney && showRepDetail && <span />}
            {showMoney && <span />}
            {showMoney && showRepDetail && <span />}
            {showMoney && showRepDetail && <span />}
            {showMoney && <span />}
          </div>
        )}

        {/* The colors & sizes editor opens here — clicks inside it stay in it. */}
        {belowQuantities && <div data-no-open>{belowQuantities}</div>}

        {/* Decoration locations + their charges */}
        {decorationsWithContent.map((d) => (
          <div key={d.id} className="mt-1 rounded-md bg-cream-50 px-2 py-0.5">
            {d.location && d.location.trim() !== "" && (
              <p className="pt-1 text-[14px] font-bold italic text-brown-400">
                {d.location}
                {colorCountWords(d.colorCount) ? ` · ${colorCountWords(d.colorCount)}` : ""}
              </p>
            )}
            {/* The location's artwork on the client's copy — their check that
                we're using the right art (Stephanie 2026-10-14). */}
            {d.artwork && (
              <div className="py-1.5">
                {d.artwork.isImage ? (
                  <img
                    src={d.artwork.url}
                    alt={`Artwork: ${d.artwork.name}`}
                    className="h-28 max-w-[220px] rounded border border-brown-100 bg-white object-contain p-1"
                  />
                ) : (
                  <a
                    href={d.artwork.url}
                    className="text-[14px] font-semibold text-brown-500 underline underline-offset-2"
                  >
                    View artwork: {d.artwork.name}
                  </a>
                )}
              </div>
            )}
            {d.charges.map((c) => {
              const amountCents = toCents(c.amount);
              // Rep-only charge COST (round-39 DocCharge.cost). ABSENT on client
              // payloads — the public route strips it via toPublicDecorationCharge
              // (qa-public-order-view/testChargeCostLeak) — and only ever read on
              // the showRepDetail branch below, so it never reaches a client.
              // Falls back to 0 so a costless charge reads $0.00 rather than blank,
              // mirroring the variant cost cell (:357-378). Margin is derived the
              // same way the variant margin cell is (:402-408); billingModel scales
              // cost and sell identically, so the per-unit ratio is the row's margin.
              const chargeCostCents = toCents(c.cost ?? 0);
              const chargeQty = chargeQuantity(c, line.quantityTotal);
              const rowTotal = amountCents * chargeQty;
              return (
                <div key={c.id} className={cn("grid items-center gap-2 py-1 text-[16px] tabular-nums", cols)}>
                  <span className="truncate">
                    {chargeLabel(c.chargeType, c.note)}
                    {/* Staff only: client payloads never carry these rows. */}
                    {c.displayToClient === false && (
                      <span className="ml-2 text-[13px] font-normal italic text-brown-300">
                        subtracted from margin
                      </span>
                    )}
                  </span>
                  <span className="text-right">
                    {chargeQty.toLocaleString()}
                  </span>
                  {showMoney && showRepDetail && (
                    <span className="text-right">{formatCents(chargeCostCents, currency)}</span>
                  )}
                  {showMoney && showRepDetail && (
                    <span className="text-right text-[14px] text-brown-300">
                      {amountCents > 0 && chargeCostCents > 0
                        ? formatPct(((amountCents - chargeCostCents) / amountCents) * 100)
                        : ""}
                    </span>
                  )}
                  {showMoney && <span className="text-right">{formatCents(amountCents, currency)}</span>}
                  {showMoney && showRepDetail && <span className="text-right" />}
                  {showMoney && (
                    <span className="text-right font-semibold">{formatCents(rowTotal, currency)}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* Per-product total — margin % and tax code sit here, as commonsku does */}
        {showMoney && (
          <div className={cn("mt-1.5 grid items-center gap-2 border-t-2 border-brown-200 pt-2 text-[15px] font-bold tabular-nums", cols)}>
            <span>Total ({currency})</span>
            <span />
            {showRepDetail && (
              <span className="text-right text-[14px] font-semibold text-brown-300">
                {formatCents(costCents, currency)}
              </span>
            )}
            {showRepDetail && (
              <span className="text-right">
                {editable && onPatchLine ? (
                  <EditableCell
                    value={marginPct == null ? "" : marginPct.toFixed(2)}
                    ariaLabel="Margin percent"
                    // The cost check belongs HERE, not in onCommit. EditableCell
                    // only reverts the draft when `validate` fails; a guard
                    // inside onCommit returns silently and leaves the typed
                    // number sitting in the box looking saved. Review traced
                    // exactly that. Failing validation reuses the existing,
                    // already-correct revert path.
                    validate={(v) => isValidMargin(v) && hasUsableCost}
                    onCommit={applyMargin}
                  />
                ) : (
                  formatPct(marginPct)
                )}
              </span>
            )}
            <span />
            {showRepDetail && (
              <span className="text-right text-[14px] font-semibold text-brown-300">
                {taxRatePct !== undefined
                  ? // Shipping is tax exempt; no rate yet → blank, never a made-up $0.
                    line.serviceType === "shipping"
                    ? "—"
                    : rateHundredths(taxRatePct) > 0
                      ? formatCents(divideRoundHalfUp(totalCents * rateHundredths(taxRatePct), 10_000), currency)
                      : taxRatePct != null && Number(taxRatePct) === 0
                        ? formatCents(0, currency)
                        : ""
                  : (line.taxCode ?? "")}
              </span>
            )}
            <span className="text-right">{formatCents(totalCents, currency)}</span>
          </div>
        )}
        {showMoney && chargesCents > 0 && (
          <p className="pt-0.5 text-right text-[12px] text-brown-300">
            includes {formatCents(chargesCents, currency)} decoration
          </p>
        )}
      </div>
    </div>
  );
}

/** Open the item unless the click landed on one of its own controls (a number
 *  box, a button, a link, a menu). Pure-ish: reads only the event target. */
function openUnlessControl(e: React.MouseEvent, open: () => void): void {
  const t = e.target as HTMLElement | null;
  if (t?.closest("input, textarea, select, button, a, label, [role='button'], [role='dialog'], [data-no-open]")) return;
  open();
}
